import { useState, useEffect, useCallback } from 'react';
import { API_URL } from 'services/api';

const CACHE_KEY = 'userIpLocation';
const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Hook to get user's location based on their IP address.
 * Results are cached in sessionStorage to avoid repeated API calls.
 * 
 * @returns {Object} { location, loading, error, refetch }
 *   - location: { countryCode, country, region, regionName, city, locationId } | null
 *   - loading: boolean
 *   - error: Error | null
 *   - refetch: function to force refresh location
 */
const useUserLocation = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLocation = useCallback(async (skipCache = false) => {
    // Check sessionStorage cache first
    if (!skipCache) {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setLocation(data);
            setLoading(false);
            return data;
          }
        }
      } catch (e) {
        // Ignore cache errors
      }
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}location/country-by-ip`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch location');
      }

      const json = await response.json();
      const locationData = json?.data || null;

      // Cache the result
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          data: locationData,
          timestamp: Date.now(),
        }));
      } catch (e) {
        // Ignore storage errors
      }

      setLocation(locationData);
      setLoading(false);
      return locationData;
    } catch (err) {
      setError(err);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  const refetch = useCallback(() => {
    return fetchLocation(true);
  }, [fetchLocation]);

  return { location, loading, error, refetch };
};

export default useUserLocation;
